// Vio's states, and what each one is allowed to mean.
//
// The eight states are not decoration. Six of them are the only signal a
// visitor gets about what happened to the text they just dropped, because the
// bubble is hidden on phones (globals.css, max-width:640px) and a screen
// reader gets the bubble's `aria-live` rather than the picture. So each state
// has to be distinguishable from the others at 104px WITHOUT colour — the
// eyes, brows and mouth carry it, and colour agrees rather than carries.
//
// The pairing below is the contract between VioFab (which decides) and
// globals.css (which draws). Adding a state means adding it in both places
// plus `VioCharacter`'s variant list, so the union is deliberately closed.

/** Every expression Vio can hold. `data-state` on the SVG root. */
export type VioState =
  | "idle" // nothing happening; eyes track the cursor, slow blink
  | "curious" // a selection exists, or a drag is in flight on the page
  | "happy" // hover, or a greeting — the reference art's own expression
  | "thinking" // working; eyes up-and-away, tracking suspended
  | "scanning" // reading the dropped text; beam sweeps the visor
  | "warning" // the input was understood and refused (wrong format, too short)
  | "success" // the text was accepted and is on its way to the scanner
  | "error"; // something broke on our side, not the visitor's

/**
 * States that suspend cursor tracking.
 *
 * A character whose eyes follow the mouse while it is meant to be thinking
 * reads as distracted, and one that tracks while showing an error reads as
 * unbothered by it. Both undercut the state they are in, so tracking is a
 * property of the state rather than a global.
 */
const UNTRACKED: ReadonlySet<VioState> = new Set([
  "thinking",
  "scanning",
  "happy",
  "success",
]);

export function tracksCursor(state: VioState): boolean {
  return !UNTRACKED.has(state);
}

/**
 * How long a transient state holds before falling back to idle.
 *
 * `warning` and `error` sit longest because they are the two a visitor has to
 * read a sentence to act on — dropping them at 2s would clear the face while
 * the eye is still on the bubble. `null` means the state is not transient:
 * something else has to end it.
 */
export const HOLD_MS: Partial<Record<VioState, number>> = {
  happy: 2200,
  success: 2600,
  warning: 6000,
  error: 6000,
  thinking: 1400,
};

/**
 * The accessible name for each state, announced alongside what Vio says.
 *
 * Without this the state change is purely visual, which on a status element
 * is the same as not happening. Kept short: it prefixes a sentence that is
 * itself being read out.
 */
export const STATE_LABEL: Record<VioState, string> = {
  idle: "",
  curious: "",
  happy: "",
  thinking: "Working",
  scanning: "Scanning",
  warning: "Note",
  success: "Accepted",
  error: "Error",
};
